import React, { FC, useState } from "react";
import { apiGetRepresentativeList } from "../api/representative";
import { useTheme } from "@mui/material/styles";
import useInfiniteScrollFetcher from "../hooks/useInfiniteScrollFetcher";
import { RepresentativeDataList } from "../interface/representative";
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  Checkbox,
} from "@mui/material";
import BadgeImage from "./ui-elements/badge-image";
import { btn_value } from "./dashboard/data";
import { btnCover } from "./styles-ui";
import { SkeletonRepsLoading } from "./ui-elements/skeleton-loading";
import useInfiniteScrollRepresentativeFetcher from "../hooks/useInfiniteScrollReps";

const initialParams = {
  page: 1,
  page_size: 10,
  search: "",
  state: "",
  local_government: "",
  position: "",
  sort_by: "asc",
  sort_order: "",
};

const RepresentativeSemicontainer = ({
  get_representative,
}: {
  get_representative: any;
}) => {
  const theme = useTheme();

  const { items, sentryRef, loading, updateParams } =
    useInfiniteScrollRepresentativeFetcher<RepresentativeDataList>({
      queryKey: "representative-data-list",
      fetchData: apiGetRepresentativeList,
      initialParams: {
        page_size: 10,
        search: "",
        state: "",
        local_government: "",
        position: "",
        sort_by: "",
        sort_order: "",
      },
    });

  const [selectedButtons, setSelectedButtons] = useState("All");

  const handleButtonClick = (value: string) => {
    setSelectedButtons(value);
    if (value === "All") {
      const param = { ...initialParams, position: "" };
      handleFilterChange(param);
      return;
    }
    //  update filter
    const param = { ...initialParams, position: value?.toLowerCase() };
    handleFilterChange(param);
  };
  // Example of updating search
  const handleSearchChange = (searchTerm: string) => {
    updateParams({ search: searchTerm });
  };

  // Example of updating multiple params
  const handleFilterChange = (filters: Partial<typeof initialParams>) => {
    updateParams(filters);
  };

  const [selectedIds, setSelectedIds] = useState<any[]>([]);

  // Handle checkbox change
  const handleCheckboxChange = (id: number | string) => {
    const get_ids = selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id) // Remove if unchecked
      : [...selectedIds, id]; // Add if checked
    setSelectedIds(
      (prev) =>
        prev.includes(id)
          ? prev.filter((selectedId) => selectedId !== id) // Remove if unchecked
          : [...prev, id] // Add if checked
    );

    // Update representative list
    get_representative(get_ids);
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(231, 235, 239, 1)",
        mt: 2,
        borderRadius: "8px",
        px: 2,
      }}
    >
      <Typography sx={{ fontWeight: 520, mt: 2 }} variant="h6" color="black">
        Your representatives
      </Typography>

      <Box sx={{ display: "flex", overflowX: "auto", my: 2 }}>
        {btn_value?.map((item, index) => (
          <Box
            key={index}
            sx={{
              ...btnCover,
              backgroundColor: selectedButtons === item ? "#459173" : "#E2F1EB",
              color: selectedButtons === item ? "white" : "#637D92",
            }}
            onClick={() => handleButtonClick(item)}
          >
            <Typography
              variant="caption"
              sx={{ whiteSpace: "nowrap", lineHeight: "10px" }}
            >
              {item}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ height: items?.length === 0 ? 200 : 400, overflowY: "auto" }}>
        {loading && items?.length === 0 && (
          <Box sx={{}}>
            {[...Array(1)].map((_, idx) => (
              <SkeletonRepsLoading key={idx} />
            ))}
          </Box>
        )}
        {!loading && items?.length === 0 && (
          <Box sx={{ mt: 5 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 500, textAlign: "center" }}
            >
              No data found
            </Typography>
          </Box>
        )}
        {items?.map((reps, id) => (
          <Stack key={reps?.id} sx={{ cursor: "pointer" }}>
            <RepresentativeSemiCover
              key={id}
              id={reps?.id}
              image={""}
              name={reps.name}
              party={reps.party}
              position={reps.position}
              badge={2}
              selectedIds={selectedIds}
              handleCheckboxChange={handleCheckboxChange}
            />
          </Stack>
        ))}
        <div ref={sentryRef} style={{}}>
          {loading && (
            <Box sx={{}}>
              <SkeletonRepsLoading />
            </Box>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default RepresentativeSemicontainer;

const RepresentativeSemiCover: FC<{
  image: any;
  name: string;
  party: string;
  position: string;
  badge: number;
  selectedIds: any[];
  handleCheckboxChange: (id: number) => void;
  id: number; // Placeholder for representative ID
}> = ({
  image,
  badge,
  name,
  party,
  position,
  id,
  selectedIds,
  handleCheckboxChange,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        my: 2,
      }}
    >
      {/* Checkbox */}
      <Checkbox
        checked={selectedIds.includes(id)}
        onChange={() => handleCheckboxChange(id)}
      />
      <Stack>
        <Stack direction={"row"} sx={{ gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} color="black">
            {name}
          </Typography>
          <BadgeImage badge={badge} />
        </Stack>
        <Typography variant="caption">{party}</Typography>
        <Typography variant="caption">{position}</Typography>
      </Stack>
    </Box>
  );
};
